<script lang="ts" setup>
    import '../../../css/choice.css';
    import { onMounted, ref, reactive, computed } from 'vue';  
    import {popAlert} from '../../lib/Notification'; 
    import {IQuerySetupResponse,IQueryInformation,IQueryTotals,IQuerySearchResponse, ErrorReturnMessage,IQueryMenuItems } from '../../lib/Models/DataQueryModel';
    import {DisplayStyle, IconTypes,TableRowType,TableRowHighlight, HoverCursorTypes } from "../../Enums/UiEnums";
    import {randomId} from "../../lib/ChoiceUtils";
    import { ICol,IRowSelection,} from "../../lib/ChoiceTableHelpers" ;
    import { IToolbar, ToolbarStyle, ToolbarSize,ToolbarBackground, IToolbarItemTypes, IToolbarActionButtonItem, IToolbarSearchActionItem, IToolbarItem, IToolbarDropDownButton } from "../../lib/Toolbar";
    import { ActionButtonStyle } from "../../lib/Types/Types";
    import Icon from "../../components/Icon.vue";  
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import DataQueryMessage from "../../components/Communication/DataQueryMessage.vue";  
    import MainWindowMessage from "../../components/Communication/MainWindowMessage.vue";  
    import Toolbar from "../../components/Toolbar.vue";
    import * as DateUtils from '../../lib/DateUtils';
    import {openWindowFromVue,checkWindowFunctionWithArgs} from '../../lib/Models/CommonItemsModel'; 
    import { getButtonClass } from '../../lib/ButtonProps';
    import * as ChoiceApi from '../../lib/ChoiceApi';
    import _ from 'lodash';
import LoadingSection from '../../components/LoadingSection.vue';
import { blankPageUrl } from '../../lib/OfficeMdUrls';

    const props = withDefaults(defineProps<{ 
        tableViewId: number,
        searchId: number,
        ignoreDefaults?: boolean,
        queryResults: IQuerySetupResponse
    }>(),
    { 
        ignoreDefaults: false
    });

    enum DataQueryToolbar {
        SearchText, 
        Search,
        Refresh, 
        Export, 
        PortalMenu, 
        ActionMenu,
        FilterTable
    };
    // These items range from 100 - 299..., Portal actions are 100-199, Opener actions are 200 - 299
    enum PortalMenuItems {
        EmrPortal = 100,
        SchedulerPortal = 101,
        BillingPortal = 102,
        Patient = 200,
        Provider = 201
    }
    // These items range from 300 - 399...
    enum ActionMenuItems {
        Action1 = 300,
        Action2 = 301,
        Action3 = 302,
        Action4 = 303,
        Action5 = 304,
    }
    enum ToolbarActionType {
        Action = "a",
        Portal = "p"
    }; 
 
    const dataQueryCommMain = ref<typeof DataQueryMessage>();
    const dataQueryScheduleCommunication = ref<typeof MainWindowMessage>();
    const dataQueryTable = ref<InstanceType<typeof ChoiceTable>>(); 
    const dataQueryComponentId = randomId(12);
    const searchInputToolTipTextPrefix = "Search for a specific value";

    const formData = reactive<
    {   
        showFrame?: boolean,  
        currentSearchId: number,
        currentSearchText: string,
        currentRowData?: any,
        divClass: {Search: string, Frame: string},
        window: {totalsHeight: number,  toolbarHeight: number, toolbarMinWidth: number, footerHeight: number, windowPadding: number, resultareaHeight: number, workareaHeight: number},
        setupData?: IQueryInformation, 
        menuList?: IQueryMenuItems[],
        totalList?: IQueryTotals[],
        resultList?: IQuerySearchResponse[],
        loadingResultList?: boolean,
        defaultFrameSize: number,
        showTableFilter?: boolean,
        highlightSearchableFields?: boolean,
        searchInputTooltip?: string
        frame: {
            src: string, 
        },
        toolbarItems: {
            searchTextInput: IToolbarSearchActionItem,
            portalMenu: IToolbarDropDownButton,
            actionMenu: IToolbarDropDownButton,
            filterTable: IToolbarActionButtonItem
        } 
    }> ({     
        showFrame: false, 
        currentSearchId: 1,
        currentSearchText: "",
        divClass: {Search: "", Frame: ""},
        window: {totalsHeight: 3, toolbarHeight: 2.75, toolbarMinWidth: 21.5, footerHeight: 1.5, windowPadding: 1.5, resultareaHeight: 10, workareaHeight: 10},
        defaultFrameSize: 55,
        frame: {src: "/officemd/screens/blank.htm"},
        searchInputTooltip: searchInputToolTipTextPrefix,
        toolbarItems:{
            searchTextInput: {
                identifier: DataQueryToolbar.SearchText, 
                tooltip: searchInputToolTipTextPrefix, 
                enabled: true, 
                search: {
                    keyPressDelay: true, 
                    placeholder: "search ...", 
                    maxLength: 200, 
                    onfocus: () => setSearchFieldFocus(true),
                    onblur: () => setSearchFieldFocus(false)
                }, 
                searchAction: performToolbarSearchText
            },
            portalMenu: { identifier: DataQueryToolbar.PortalMenu, label: "", tooltip: "Portal Opener", action: ActionButtonStyle.Open, enabled: false, hidden: true, menuList: [] },
            actionMenu: { identifier: DataQueryToolbar.ActionMenu, label: "", tooltip: "Other Actions", action: ActionButtonStyle.Ellipsis, enabled: false, hidden: true, menuList: [] },
            filterTable: { identifier: DataQueryToolbar.FilterTable, label: "", tooltip: "Filter Table", action: ActionButtonStyle.Filter, enabled: true, hidden: false, performAction: onClickFilterButton }
        }
    });
 
    // Toolbar configuration
    const toolbarConfiguration = computed<IToolbar>(() =>
        ({toolbarList: [
            formData.toolbarItems.searchTextInput,
            {identifier: DataQueryToolbar.Refresh, label: "", tooltip: "Refresh list", action: ActionButtonStyle.Refresh, enabled: true, performAction: newQueryResults},
            {identifier: DataQueryToolbar.Export, label: "", tooltip: "Export list to Excel", action: ActionButtonStyle.Excel, enabled: true, performAction: exportReport},
            formData.toolbarItems.portalMenu,
            formData.toolbarItems.actionMenu,
            formData.toolbarItems.filterTable
        ]})
    );    
    
    onMounted(async () => {  
        updateSearchInputTooltip();

        formData.currentSearchId = props.queryResults.setupData?.searchID ?? 1;
        formData.setupData = props.queryResults.setupData;
        formData.menuList = props.queryResults.menuList;
        formData.frame.src = `${formData.setupData.screenName}?DQC=${dataQueryComponentId}`;
        formData.totalList = props.queryResults.totalList;
        formData.resultList = props.queryResults.resultList;
        formData.loadingResultList = false;
        formData.showFrame = false; 
        formData.divClass.Search = "label-isalus-small col";
        formData.divClass.Frame = "label-isalus-small text-primary skinnyscroll frame-css col";

        resetHeights();
        window.addEventListener('resize', () => {resetHeights();});
        ParentWindowCallback(); 
        toolbarSetup();  
    }); 
    function toolbarSetup() : void { 
        toolbarDropDownSetup(ToolbarActionType.Portal, formData.toolbarItems.portalMenu); 
        toolbarDropDownSetup(ToolbarActionType.Action, formData.toolbarItems.actionMenu);
    }
    function resetFrameWidth() : void {
        const minSize = formData.setupData?.minFrameSize ?? formData.defaultFrameSize; 
        const windowWidth = window.innerWidth / 16;
        const frameWidth = Math.round((minSize / windowWidth) * 12);
        const searchWidth = 12 - frameWidth;
        formData.divClass.Search = "label-isalus-small " + (formData.showFrame ? `col-${searchWidth}` : "col");
        formData.divClass.Frame = "label-isalus-small text-primary skinnyscroll frame-css " + (formData.showFrame ? `col-${frameWidth}` : "col"); 
    }
    function resetHeights() : void {
        const windowHeight = window.innerHeight / 16;
        
        //formData.window.totalsHeight = ((formData.totalList?.length ?? 1) * 1.5) + .5;
        formData.window.totalsHeight = (formData.totalList?.length ?? 1) + 1;
        formData.window.workareaHeight = windowHeight; 
        formData.window.resultareaHeight = windowHeight - formData.window.totalsHeight - formData.window.toolbarHeight - formData.window.footerHeight; 
        resetFrameWidth();
    }
    async function refreshQueryTotals() : Promise<void> {     
        const respGet = await ChoiceApi.dataQuery.getTotals(props.tableViewId, formData.currentSearchId, props.ignoreDefaults);  
        if (!respGet.success) {  
            popAlert(respGet.error?.error ?? ErrorReturnMessage.totals,"Refresh Totals");
            return;
        } 
        formData.totalList = respGet.success.totalList; 
    }  
    async function newQueryResults() : Promise<void> {    
        formData.loadingResultList = true;
        const respGet = await ChoiceApi.dataQuery.getResults(props.tableViewId, formData.currentSearchId, formData.currentSearchText, props.ignoreDefaults);  
        formData.loadingResultList = false;        

        if (!respGet.success) {  
            popAlert(respGet.error?.error ?? ErrorReturnMessage.results,"Refresh Totals");
            return;
        } 
        formData.resultList = respGet.success.resultList;
    }  
    function switchSearchId(newSearchId :number) : void {
        if (newSearchId !== formData.currentSearchId) {
            FrameDisplay(false);
            formData.currentRowData = undefined;
            formData.currentSearchId = newSearchId;
            clearMenuSearchText(true);
        }
    }
    function getRowValue(identifier: string, rowData: IRowSelection & Record<string, any>) : string | number | Date | undefined  {
        if (!formData.currentRowData) {
            return undefined;
        }
        if (identifier in rowData) { 
            return rowData[identifier]; 
        }
        return undefined;
    }
    function queryRowdblclick(rowData : IRowSelection,  _rowType: TableRowType, _evt: MouseEvent) : void {
        formData.currentRowData = (rowData as any);
        if (formData.setupData) {
            const screenColumnValue = getRowValue(formData.setupData?.screenKeyColumn, formData.currentRowData);
            if (screenColumnValue) {
                dataQueryCommMain.value?.openDataQuery({data: {screenColumnValue: screenColumnValue, SearchID: formData.currentSearchId},dataQueryComponentId: dataQueryComponentId});
                FrameDisplay(true);
            }
        }
    }  
    function FrameDisplay(showFrame: boolean) : void {
        formData.showFrame = showFrame;
        resetFrameWidth();     
    } 
    function hideDataQueryView(payload: unknown) : void {
        FrameDisplay(false);
    }
    async function toolbarClick(data: DataQueryToolbar | PortalMenuItems | ActionMenuItems): Promise<void> {  
        switch(data)
        {
            // Click the default "Portal Opener" button (not a menu item)
            case DataQueryToolbar.PortalMenu:
            toolbarDropDownExecute(ToolbarActionType.Portal);
            return; 
            
            // Click the default "Other Actions" button (not a menu item)
            case DataQueryToolbar.ActionMenu:
            toolbarDropDownExecute(ToolbarActionType.Action);
            return;

            // Click a "Other Actions" menu item
            case ActionMenuItems.Action1:
            case ActionMenuItems.Action2:
            case ActionMenuItems.Action3:
            case ActionMenuItems.Action4:
            case ActionMenuItems.Action5:
            actionToolbarClick(data);  
            return;

            // Click a "Portal Opener" menu item to load a page
            case PortalMenuItems.Patient:
            case PortalMenuItems.Provider:
            openerToolbarClick(data);
            return;

            // Click a "Portal Opener" menu item to open a portal
            case PortalMenuItems.EmrPortal:
            case PortalMenuItems.SchedulerPortal:
            case PortalMenuItems.BillingPortal:
            portalToolbarClick(data); 
            return;

            default:
            popAlert("Invalid Menu selection.","Data Menu");
            return;
        }
    }

    async function onClickFilterButton()
    {
        formData.showTableFilter = !formData.showTableFilter;
        updateFilterButtonClass();
    }

    // Update the button class based on if the table filter is on or off
    function updateFilterButtonClass()
    {
        formData.toolbarItems.filterTable.buttonClass = getFilterButtonClass();

        // If the table isn't being filtered, make sure to clear the filter
        if (!formData.showTableFilter)
        {
            dataQueryTable.value?.clearFilterValues();
        }
    }

    function getFilterButtonClass(): string|undefined
    {
        if (!formData.showTableFilter)
                return undefined;
        return [getButtonClass({ action: ActionButtonStyle.Filter, displayStyle: DisplayStyle.Primary, text: ""}), "label-isalus-small"].join(" ");        
    }

    async function clearMenuSearchText(perforSearch : boolean) : Promise<void> {

        const searchToolbarItem = formData.toolbarItems.searchTextInput.search;
        if (searchToolbarItem)
        {
            searchToolbarItem.searchText = "";
            formData.currentSearchText = searchToolbarItem.searchText ?? "";
            if (perforSearch) {
                await newQueryResults();
            }
        }

        formData.showTableFilter = false;
        updateFilterButtonClass();
    }

    // Portal Menu Items
    async function portalToolbarClick(data:  PortalMenuItems): Promise<void> { 
        if (data == PortalMenuItems.EmrPortal) {         
            const patientEid = await getValidPatientEntityId();
            if (!patientEid) 
                return;

            dataQueryScheduleCommunication.value?.openEmr({patientEid: patientEid, scheduleDate: DateUtils.getNoTzToday()});
            return;
        }
        if (data == PortalMenuItems.SchedulerPortal) {
            const patientEid = await getValidPatientEntityId();
            if (!patientEid) 
                return;

            dataQueryScheduleCommunication.value?.openSchedule({patientEid: patientEid});
            return;
        }
        if (data == PortalMenuItems.BillingPortal) {
            popAlert("Unable to open patient in Billing, this process is not setup.","Billing");
            return;
        }
        popAlert("Invalid Portal Menu selection.","Data Query Portal");
    }

    async function getValidPatientEntityId(): Promise<number|undefined> {
        if (!formData.currentRowData)
        {
            await popAlert("Unable to open patient in EMR.  You need to select a row.","EMR");
            return;
        }

        let patientEid = getRowValue("patient_eid", formData.currentRowData);
        if (patientEid)
            patientEid = +patientEid;
        
        if (!patientEid)
        {
            await popAlert("Unable to open patient in scheduler.","Scheduler");
            return;
        }
        return +patientEid;
    }

    // Opener Menu Items 
    async function openerToolbarClick(data:  PortalMenuItems): Promise<void> {  
        if (!formData.currentRowData) {
            popAlert("Please select an record.","Open");
            return;
        }
        if (data == PortalMenuItems.Patient) {
            const patientId = getRowValue("patient_id", formData.currentRowData);
            if (patientId) {
                const patientURL = `/officemd/screens/WindowSetup.htm?WT=Patient Setup&Qualifier=patient&WindowID=${patientId}&tab=Demographics`;
                dataQueryWindowOpen("NewPatient", patientURL); 
            }
            else {
                popAlert("Unable to open patient setup.","Open Patient");
            }
            return;
        }
        if (data == PortalMenuItems.Provider) {
            const providerID = getRowValue("billing_provider_id", formData.currentRowData);
            if (providerID) {
                const providerURL = `/officemd/screens/provider.asp?WT=Provider Setup&Qualifier=provider&WindowID=${providerID}`;
                dataQueryWindowOpen("Provider", providerURL);
            }
            else {
                popAlert("Unable to open provider setup.","Open Provider");
            }
            return;
        }
        popAlert("Invalid Open Menu selection.","Data Query Opener");
    }
    // Action Menu Items
    async function actionToolbarClick(data:  DataQueryToolbar | PortalMenuItems | ActionMenuItems ): Promise<void> { 
        if (data == ActionMenuItems.Action1) {
            alert("Action1");
            return;
        }
        if (data == ActionMenuItems.Action2) {
            alert("Action2");
            return;
        }
        popAlert("Invalid Action Menu selection.","Data Query Action");
    }
    async function toolbarDropDownExecute(actionType: ToolbarActionType) {
        const firstMenuItem = formData.menuList?.find(item => item.actionType === actionType);
        if (firstMenuItem) {
            if (firstMenuItem.menuID >= ActionMenuItems.Action1)
                actionToolbarClick(firstMenuItem.menuID);
            else if (firstMenuItem.menuID >= PortalMenuItems.EmrPortal)
                portalToolbarClick(firstMenuItem.menuID);
            else {
                popAlert("Invalid Menu Selection.","Data Query Action");
            }
        }
    }

    function toolbarDropDownSetup(actionType: ToolbarActionType, toolbarItem : IToolbarDropDownButton) {
        const ddMenuList = formData.menuList?.filter(item => item.actionType === actionType).map((item) => ({identifier: item.menuID, label: item.label}));
        if ((ddMenuList) && (ddMenuList.length > 0)) {
            formData.window.toolbarMinWidth += 4.5;
            toolbarItem.menuList = ddMenuList;
            toolbarItem.enabled = true; 
            toolbarItem.hidden = false; 
        }
    }

    async function performToolbarSearchText(identifier: string|number|undefined, searchResult: string|undefined, forceNewSearch?: boolean): Promise<void> { 
      if ((searchResult && formData.currentSearchText !== searchResult) || forceNewSearch) {
        formData.currentSearchText = searchResult ?? "";
        await newQueryResults();
      }
    }

    async function exportReport() : Promise<void>  { 
        const sheet_name = `${formData.setupData?.description}`;
        const file_name = `${formData.setupData?.description}`;
        dataQueryTable.value?.exportToExcel(file_name, sheet_name);
    } 
    function ParentWindowCallback() : void { 
        const DQ_function = checkWindowFunctionWithArgs<() => void>('DQ_Enable',true);
        if (DQ_function) {
            DQ_function();
        } 
    }
    function dataQueryWindowOpen(windowID: string, windowURL: string) { 
        openWindowFromVue(windowID, windowURL,{ noTimestamp: "Y" });
    }

    const currentIFrameSrc = computed(() =>{
        if (!formData.showFrame)
        {
            return blankPageUrl();
        }

        let url = formData.frame.src;
        for(const fieldName in formData.currentRowData ?? {})
        {
            url = url.replaceAll("{" + fieldName + "}", formData.currentRowData[fieldName] ?? "");
        }
        return url;
    });

    // Get the columns for the results table
    const visibleDataTableColumns = computed<ICol[]>(() => {
        const tableColumns = props.queryResults.columnList
                                    .filter(item => !item.isHidden)
                                    .map(item => ({ 
                                        Name: item.columnTitle, 
                                        DataField: item.columnName, 
                                        HideFilter: item.isSearchable, 
                                        display_order: item.displayOrder,
                                        StyleDel: resultTableColumnStyleDelegate
                                     }));

        return _.orderBy(tableColumns, item => item.display_order);
    });

    // Set the header style when the search fields are to be highlighted
    function resultTableColumnStyleDelegate(col: ICol, row: Record<string, any>, rowType: TableRowType)
    {
        if (!formData.highlightSearchableFields)
            return;

        if (TableRowType.Header === rowType)
        {
            var dataCol = props.queryResults.columnList.find(dataCol => dataCol.columnName === col.DataField);
            if (!dataCol?.isSearchable)
            {
                // Make the non-searchable fields less prominent
                return { 
                    ["font-size"]: "0.9em",
                    ["opacity"]: "0.5"
                };
            }
        }
    }

    const searchInputToolTipText = computed<string>(() => {
        const searchableColumnTitles = props.queryResults.columnList
                    .filter(item => item.isSearchable && !item.isHidden)
                    .map(item => item.columnTitle);
        return `Search is limited these fields:\n\t${searchableColumnTitles.join("\n\t")}`;
    });

    function updateSearchInputTooltip()
    {
        formData.toolbarItems.searchTextInput.tooltip = searchInputToolTipText.value;
    }

    function setSearchFieldFocus(hasFocus: boolean)
    {
        formData.highlightSearchableFields = hasFocus;
    }

    function dataTableRowClass(row: Record<string, any>): string | undefined
    {
        return "pointer";
    }
</script>

<style scoped>
    .search-totals {
        background-color: #eef5ff;
        border-bottom: 1px solid #6a8ccb;
    }
    .search-toolbar {
        background-color: #dbebff;
        border-bottom: 1px solid #6a8ccb;
    }
    .search-results {
        background-color: white;
        border-bottom: 1px solid #6a8ccb;
    }
    .search-footer {
        background-color: #dbebff;
        border-bottom: 1px solid #6a8ccb;
    }
    .frame-css {
        background-color: white;
        border-left: 1px solid #6a8ccb; 
    }

    /* Totals */
    .totals-container {
        display: flex;
        flex-direction: column;
        width: 100%;
    }
    
    .totals-row {
        display: flex;
        /*align-items: center;*/
        width: 100%;
    }
    
    .icon, .status, .number {
        display: inline-block;
        cursor: pointer;
    }

    .label-text {
        cursor: pointer;
    }
    
    .icon, .status {
        width: 1.25rem;
    }
    
    .number {
        width: 1.75rem;
    }
    
    .label {
        flex-grow: 1; /* Takes remaining space */
    }    
    
</style>
<template> 
    <div class="row">
        <div :class="formData.divClass.Search" :style="{'overflow-y': 'hidden', 'overflow-x': 'hidden', 'height': (formData.window.workareaHeight) + 'rem'}">
            <div class="row search-totals"> 
                <div class="col label-isalus-small" 
                    :style="{'overflow-y': 'hidden', 'overflow-x': 'hidden', 'height': (formData.window.totalsHeight) + 'rem', 'padding-top': '0.25em'}">
                    <div class="totals-container">
                        <div class="totals-row" v-for="(totalsRecord,index) in formData.totalList">
                            <div class="icon">
                                <Icon 
                                    v-if="0 === index" 
                                    :hoverCursor="HoverCursorTypes.Pointer"
                                    :icon="IconTypes.Refresh" 
                                    :displayStyle="DisplayStyle.Primary" 
                                    :class="'text-isalus-primary'"
                                    :title="'Refresh all totals'"
                                    @click="refreshQueryTotals"/>                        
                            </div>
                            <div class="status" @click="switchSearchId(totalsRecord.searchID)">
                                <Icon v-if="totalsRecord.searchID === formData.currentSearchId" 
                                    :icon="IconTypes.Ready" 
                                    :displayStyle="DisplayStyle.Success" 
                                    :class="'text-success'"/> 
                            </div>
                            <div class="number" @click="switchSearchId(totalsRecord.searchID)">
                                <span v-show="totalsRecord.totalCount < 99">{{totalsRecord.totalCount}}</span>
                                <span v-show="totalsRecord.totalCount >= 99">99+</span>                                 
                            </div>
                            <div class="label">
                                <span class="label-text" @click="switchSearchId(totalsRecord.searchID)">
                                    {{totalsRecord.description}}                                
                                </span>
                            </div>
                        </div>
                    </div>                    
                </div>
            </div>
            <div class="row search-toolbar">
                <div class="col text-isalus-primary " :style="{'min-width': (formData.window.toolbarMinWidth) + 'rem', 'height': (formData.window.toolbarHeight) + 'rem'}">
                    <Toolbar 
                        :configuration="toolbarConfiguration" 
                        :toolbarSize="ToolbarSize.regular" 
                        :toolbarStyle="ToolbarStyle.outline" 
                        :toolbarBackground="ToolbarBackground.white"
                        @toolbarClick="toolbarClick" />
                </div>
            </div>
            <div class="row search-results"> 
                <div class="col label-isalus-small text-isalus-primary skinnyscroll pt-1" :style="{'overflow-y': 'auto', 'overflow-x': 'auto','font-size': '.75rem', 'height': (formData.window.resultareaHeight) + 'rem'}">
                    <LoadingSection :isLoading="formData.loadingResultList" loadingMsg="Loading items...">
                        <ChoiceTable
                            ref="dataQueryTable"
                            :columns="visibleDataTableColumns" 
                            :data="formData.resultList" 
                            :show-filters="formData.showTableFilter" 
                            :allowSorting="true"
                            :key-value-data-member="formData.setupData?.tableKeyColumn ?? ''"
                            :rowHighlight="TableRowHighlight.onDoubleClick"
                            :rowBodyClass="dataTableRowClass"
                            @rowdblclick="queryRowdblclick" /> 
                    </LoadingSection>
                </div>
            </div>
            <div class="row search-footer">
                <div class="col label-isalus-small fw-bold text-isalus-primary " :style="{'overflow-y': 'hidden', 'overflow-x': 'hidden', 'height': (formData.window.footerHeight) + 'rem'}">
                    {{((formData.resultList?.length ?? 0) == 0 ? "No Results" : formData.resultList?.length + " items")}}
                </div>
            </div>
        </div> 
        <div v-show="formData.showFrame" :class="formData.divClass.Frame" :style="{'overflow-y': 'hidden', 'overflow-x': 'hidden', 'height': (formData.window.workareaHeight) + 'rem'}">
            <iframe id="fQueryView" name="fQueryView" :src="currentIFrameSrc"  title="Data Query View" style="position: relative; top: 0px; left: 0px; width: 100%; height: 100%"></iframe>
        </div> 
    </div>  
    <DataQueryMessage ref="dataQueryCommMain" :dataQueryParent="true" :componentID="dataQueryComponentId" @closeDataQuery="hideDataQueryView"/> 
    <MainWindowMessage ref="dataQueryScheduleCommunication"/>  
</template> 